<template>
  <div
    class="LiteProductCard"
    :class="{
      'LiteProductCard--fade-contents':
        activeVariant && !activeVariant.inStock && !activeVariant.onPreorder,
      'LiteProductCard--compact-view': compactView,
      'LiteProductCard--with-discount': showDiscountedPrice,
      'LiteProductCard--with-swatches': hasCustomOptions,
    }"
    @mouseenter="toggleFrontAngle(true)"
    @mouseleave="handleCardMouseLeave()"
  >
    <div
      :class="[
        'LiteProductCard__media',
        {
          'LiteProductCard__media--loading': imageLoading,
          'LiteProductCard__media--front-angle': showFrontAngle,
          'LiteProductCard__media--front-angle-loaded':
            frontAngleLoaded && showFrontAngle,
        },
      ]"
    >
      <VisionLink
        :to="urlForVariant"
        display="block"
        class="LiteProductCard__media-link"
        @click="handleClick('image', product.index + 1)"
      >
          <img
            v-if="activeVariant.staticImage?.url"
            :src="activeVariant.staticImage?.url"
            alt="SunGod E-Gift Card"
            :width="800"
            :height="800"
            loading="lazy"
          />
          <DynamicImg
            v-if="
              previousVariant?.legacySKU && !imageLoading && !showFrontAngle
            "
            :sku="previousVariant?.legacySKU"
            :width="800"
            :height="800"
            bg="transparent"
            lazy-load
            class="LiteProductCard__image LiteProductCard__image--previous-image"
          />
          <DynamicImg
            v-if="activeVariant.legacySKU"
            :sku="activeVariant.legacySKU"
            :width="800"
            :height="800"
            bg="transparent"
            lazy-load
            :class="[
              'LiteProductCard__image',
              'LiteProductCard__image--side-angle',
            ]"
            @loaded="handleNewImageLoaded"
          />
          <div class="LiteProductCard__front-angle-animation-wrapper">
            <DynamicImg
              v-if="activeVariant.legacySKU && showFrontAngle"
              :sku="activeVariant.legacySKU"
              :width="800"
              :height="800"
              bg="transparent"
              lazy-load
              :view-angle="2"
              class="LiteProductCard__image LiteProductCard__image--front-angle"
              @loaded="
                () => {
                  ;(frontAngleLoaded = true), (imageLoading = false)
                }
              "
            />
          </div>
        <div
          v-if="activeVariant.logo?.responsiveImage"
          class="LiteProductCard__media-overlay-item"
        >
          <DatoCmsImage
            :data="activeVariant.logo.responsiveImage"
            :fade-in-duration="200"
            sizes="200px"
          />
        </div>
        <VisionTag
          v-if="lensTechBadgeSuffixText || lensTechBadgeIcon"
          class="LiteProductCard__lens-tech-badge"
          size="small"
          theme="none"
          :with-gradient-text="false"
        >
          <span
            :class="[
              'LiteProductCard__lens-tech-badge-content',
              {
                'LiteProductCard__lens-tech-badge-content--no-icon':
                  !lensTechBadgeIcon,
              },
            ]"
          >
            <VisionIcon
              v-if="lensTechBadgeIcon"
              :id="product.sku + '-lite-product-card-lens-tech-icon'"
              :icon="lensTechBadgeIcon"
              gradient="default"
              size="stretch"
              class="LiteProductCard__lens-tech-badge-icon"
              :class="{
                'LiteProductCard__lens-tech-badge--snow': isSnowLensTech,
              }"
            />
            {{ lensTechBadgeSuffixText }}
          </span>
        </VisionTag>
      </VisionLink>
      <VisionLink
        v-if="hasCustomOptions && !compactView"
        class="LiteProductCard__custom-btn"
        :to="getUrlForVariant(activeVariant, { customiser: true })"
      >
        <span class="LiteProductCard__custom-btn-text"> Customise </span>
        <span class="LiteProductCard__custom-btn-icon-wrapper">
          <div class="LiteProductCard__custom-btn-icon">
            <CustomIcon :id="product.sku"/>
          </div>
        </span>
      </VisionLink>
    </div>
    <div class="LiteProductCard__body">
      <div class="LiteProductCard__title-wrapper">
        <VisionLink
          v-if="activeVariant.name"
          :to="urlForVariant"
          display="block"
          class="LiteProductCard__title"
          @click="handleClick('title', product.index + 1)"
        >
          <span class="LiteProductCard__title-content">
            {{ activeVariant.name }}
            <span
              v-if="activeVariant.nameSuffix"
              class="LiteProductCard__title-suffix"
            >
              {{ activeVariant.nameSuffix }}
            </span>
            <div class="LiteProductCard__price">
              <span
                v-if="showDiscountedPrice"
                class="LiteProductCard__discounted-price"
              >
                {{ $tca(discountedPriceFromResult) }}
              </span>
              <component
                :is="showDiscountedPrice ? 's' : 'span'"
                class="LiteProductCard__base-price"
              >
                {{ $tca(priceFromResult) }}
              </component>
            </div>
          </span>
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-if="subtitleForVariant"
            class="LiteProductCard__subtitle"
            v-html="subtitleForVariant"
          />
          <!-- eslint-enable vue/no-v-html -->
        </VisionLink>
      </div>
      <div class="LiteProductCard__variants-wrapper">
        <div
          v-if="variantsToDisplay.length > 1 && showSwatchSelector"
          class="LiteProductCard__variants"
        >
          <template
            v-for="variant in variantsToDisplay"
            :key="variant.sku"
          >
            <VisionLink
              :class="[
                'LiteProductCard__variant',
                {
                  'LiteProductCard__variant--active':
                    variant.sku === activeVariant.sku
                },
              ]"
              :to="getUrlForVariant(activeVariant)"
              @mouseenter="hoveredVariant(variant.sku)"
              @click="selectedVariant(variant.sku)"
            >
              <DynamicImg
                v-if="variant.legacySKU"
                :sku="variant.legacySKU"
                :width="200"
                :height="200"
                :view-angle="2"
                bg="transparent"
                lazy-load
                class="LiteProductCard__variant-image"
              />
            </VisionLink>
          </template>
        </div>
        <VisionLink
          v-if="hasCustomOptions"
          class="LiteProductCard__variant LiteProductCard__variant--custom"
          :to="getUrlForVariant(activeVariant, { customiser: true })"
          :mono-font="true"
        >
          +2
        </VisionLink>
      </div>
    </div>
    <VisionTag
      v-if="calculatedTag.text"
      :text="calculatedTag.text"
      :theme="calculatedTag.theme || 'default'"
      size="small"
      class="LiteProductCard__tag"
    />
  </div>
</template>
<script setup lang="ts">
import { Image as DatoCmsImage, type ResponsiveImageType } from 'vue-datocms'
import { filterValidLensTechDisplay } from '~/assets/js/helpers/search/search'

interface Reviews {
  count: number
  averageStars: number
}

interface CardTag {
  text: string
  theme: string
}

interface LiteProductCard {
  sku: string
  legacySKU: string
  name: string
  nameSuffix: string
  subtitle: string
  logo: { responsiveImage: ResponsiveImageType }
  url: string
  prices: { [key: string]: number }
  discountedPrices: object
  isLimitedEdition: boolean
  inStock: boolean
  onPreorder: boolean
  variants: Array<any>
  reviews: Reviews
  index: number
  tag: CardTag
  promoTag: CardTag
  lensTech: { filterValue: string; displayValue: string }
  showOutOfStock: boolean
  respectLensTech: boolean
}

interface Props {
  product: LiteProductCard
  showSwatchSelector?: boolean
  isFilterResult?: boolean
  compactView?: false
}

const { isSmBreakpointOrAbove } = useBreakpoint()

const props = withDefaults(defineProps<Props>(), {
  product: () => {},
  showSwatchSelector: true,
  isFilterResult: false,
  compactView: false,
})
const emits = defineEmits(['click-giftcard', 'click'])

const { $i18n, $tca, $track } = useNuxtApp()
const shopifyStore = useShopifyStore()

const activeVariant = toRef(props.product)
const previousVariant = ref(props.product)
const imageLoading = ref(false)
const showFrontAngle = ref(false)
const frontAngleLoaded = ref(false)

const discountsForProduct = ref()
discountsForProduct.value = shopifyStore.getDiscountsFromProduct(null, {
  sku: props.product.sku,
  price: props.product.prices['GBP'],
})

watch(
  () => props.product,
  (newValue) => {
    activeVariant.value = newValue
  }
)

watch(
  () => activeVariant.value.sku,
  (_newValue, oldValue) => {
    previousVariant.value = oldValue
    imageLoading.value = true
  }
)

watch(
  () => imageLoading.value,
  () => {
    setTimeout(() => {
      imageLoading.value = false
    }, 10000)
  }
)

const variantsToDisplay = computed(() => {
  /**
   *
   * 1. Keep variants that are in stock or on preorder
   * 2. Always keep the variant that is related to the parent product
   * 3. If it is a limited edition product show all limited editions up to four. If less than four limited editions add 1 normal hero swatch for each less than 4.
   * 4. If it is a non limited edition show first 4 hero designs
   *
   */
  const maxVariantsToDisplay = 4
  const newVariants = []

  const availableVariants = props.product.variants.filter(
    (variant) =>
      variant.inStock ||
      variant.onPreorder ||
      variant.showOutOfStock ||
      props.product.sku === activeVariant.value?.sku ||
      variant.static
  )

  if (props.product.isLimitedEdition) {
    // Push limited edition products up to the allowed amount into the newVariants array
    availableVariants.forEach((availableVariant) => {
      if (
        availableVariant.isLimitedEdition &&
        newVariants.length < maxVariantsToDisplay
      ) {
        newVariants.push(availableVariant)
      }
    })

    // If the parent product is not included in the newVariants array then add
    if (
      newVariants.findIndex(
        (newVariant) => newVariant.sku === props.product.sku
      ) <= -1
    ) {
      newVariants.unshift(
        availableVariants.find(
          (availableVariant) => availableVariant.sku === props.product.sku
        )
      )
    }

    // Add a normal swatch if limited edition swatches are less than the total allowed
    if (newVariants.length < maxVariantsToDisplay) {
      newVariants.unshift(
        ...availableVariants
          .filter((availableVariant) => !availableVariant.isLimitedEdition)
          .slice(0, maxVariantsToDisplay - newVariants.length)
      )
    }

    // Only return first [maxVariantsToDisplay] from the newVarients array
    return newVariants.slice(0, maxVariantsToDisplay).filter((a) => a)
  }

  /**
   *
   *  (Below)
   *
   *  Normal swatches
   *
   * Return [maxVariantsToDisplay] normal swatches
   * Always include parent product variant in that [maxVariantsToDisplay]
   *
   */

  const parentProductIndex = availableVariants.findIndex(
    (availableVariant) => availableVariant.sku === props.product.sku
  )

  if (parentProductIndex + 1 > maxVariantsToDisplay) {
    availableVariants.unshift(availableVariants[parentProductIndex])
  }

  //Push static variants to array
  const staticVariants = availableVariants.filter((variant) => variant.static)

  if (staticVariants.length) {
    const newVariants = [
      ...availableVariants.splice(
        0,
        maxVariantsToDisplay - staticVariants.length
      ),
      ...staticVariants,
      ...availableVariants.splice(
        staticVariants.length - 1,
        maxVariantsToDisplay
      ),
    ]

    return newVariants.slice(0, maxVariantsToDisplay)
  }

  return availableVariants.slice(0, maxVariantsToDisplay)
})

const onlyLimitedEdition = computed(() => {
  return (
    variantsToDisplay.value.filter((variant) => variant.isLimitedEdition)
      .length === variantsToDisplay.value.length
  )
})

const priceFromResult = computed(() => {
  return activeVariant.value.prices[$i18n.localeProperties.value.currency]
})

const discountedPriceFromResult = computed(() => {
  if (!activeVariant.value.discountedPrices) return 0

  return activeVariant.value.discountedPrices[
    $i18n.localeProperties.value.currency
  ]
})

// if discounted price is less than base price but more than 0
const showDiscountedPrice = computed(() => {
  return (
    discountedPriceFromResult.value < priceFromResult.value &&
    discountedPriceFromResult.value > 0
  )
})

function getUrlForVariant(variant: {
  url: string
  sku: string
  static: boolean
}, options?: { customiser: boolean }) {
  if (variant.static) return variant.url

  const urlObj = new URL(props.product.url, 'https://www.sungod.co')
  urlObj.searchParams.set('sku', variant.sku)

  if (props.product.respectLensTech) {
    urlObj.searchParams.set('rlt', '1')
  }

  if (options?.customiser) {
    urlObj.searchParams.set('customiser', '1')
  }

  return urlObj.pathname + urlObj.search
}

const urlForVariant = computed(() => {
  return getUrlForVariant(activeVariant.value)
})

async function selectedVariant(sku: LiteProductCard['sku']) {
  activeVariant.value = props.product.variants.find(
    (variant) => variant.sku === sku
  )

  await $track.global.linkClicked(
    {
      interactionElement: 'Clicked swatch',
      interactionCategory: 'Product card',
    },
    'Clicked Product Card Swatch'
  )
}

function hoveredVariant(sku: LiteProductCard['sku']) {
  if (!isSmBreakpointOrAbove.value) return

  activeVariant.value = props.product.variants.find(
    (variant) => variant.sku === sku
  )
}

function handleNewImageLoaded() {
  if (!isSmBreakpointOrAbove.value) return

  imageLoading.value = false
  previousVariant.value = null
}

function handleCardMouseLeave() {
  if (!isSmBreakpointOrAbove.value) return

  if (!frontAngleLoaded.value) {
    imageLoading.value = false
  }

  previousVariant.value = null

  showFrontAngle.value = false
  frontAngleLoaded.value = false
}

function toggleFrontAngle(value: boolean) {
  if (!isSmBreakpointOrAbove.value) return

  imageLoading.value = value
  showFrontAngle.value = value
}

const subtitleForVariant = computed(() => {
  if (
    activeVariant.value.isLimitedEdition ||
    !activeVariant.value.lensColour ||
    !activeVariant.value.frameColour
  ) {
    return activeVariant.value.productInfoTitle
  } else if (variantsToDisplay.value?.length > 0) {
    return '+5 Colours & Custom'
  } else if (
    activeVariant.value.static &&
    activeVariant.value.productInfoTitle
  ) {
    return activeVariant.value.productInfoTitle
  } else {
    let subtitle = ''

    subtitle += activeVariant.value.frameColour + ' with '

    if (
      filterValidLensTechDisplay(activeVariant.value.lensTech) &&
      activeVariant.value.lensTech.displayValue
    ) {
      subtitle += activeVariant.value.lensTech.displayValue + ' '
    }

    return (subtitle += activeVariant.value.lensColour)
  }
})

const calculatedTag = computed(() => {
  const hasPromoTagText = !!activeVariant.value.promoTag?.text
  // Exclude prescription
  // !/le_STCRXclear|le_THCRXclear|le_RXclear|le_STRX|le_STPRX|le_THRX|le_THPRX|le_RX/.test(props.product.sku)
  if (!activeVariant.value) return

  if (!activeVariant.value.inStock && !activeVariant.value.onPreorder) {
    return { text: 'Sold out', theme: 'white' }
  }

  if (
    hasPromoTagText &&
    discountsForProduct.value.anyDiscountsAreFreeAndOverThreshold
  ) {
    return {
      text: activeVariant.value.promoTag?.text,
      theme:
        activeVariant.value.promoTag?.theme === 'special' ? 'promo' : 'default',
    }
  }

  if (activeVariant.value.tag?.text) return activeVariant.value.tag

  return {}
})

const hasCustomOptions = computed(() => {
  return variantsToDisplay.value.length && !onlyLimitedEdition.value
})

const lensTechBadgeIcon: ComputedRef = computed(() => {
  if (['8ko', '8kosnow'].includes(activeVariant.value.lensTech?.filterValue))
    return activeVariant.value.lensTech?.filterValue

  if (['8kop'].includes(activeVariant.value.lensTech?.filterValue)) return '8ko'

  return ''
})

const isSnowLensTech = computed(() => {
  return ['8kosnow'].includes(activeVariant.value.lensTech?.filterValue)
})

const lensTechBadgeSuffixText = computed(() => {
  if (
    ['4ko', '4kosnow', 'rx', 'rxClear'].includes(
      activeVariant.value.lensTech?.filterValue
    )
  )
    return

  if (['4kop', '8kop'].includes(activeVariant.value.lensTech?.filterValue))
    return 'Polarised'

  return ''
})

async function handleClick(
  elClickedOn: 'image' | 'title',
  numberInList: number
) {
  if (props.isFilterResult) {
    await $track.filter.resultClicked({
      resultElementClicked: elClickedOn,
      numberInList: numberInList,
    })
  }

  if (activeVariant.value.event?.name === 'click-giftcard') {
    emits(
      'click-giftcard',
      activeVariant.value.prices[$i18n.localeProperties.value.currency]
    )
  }

  emits('click')
}
</script>

<style lang="postcss" scoped>
.LiteProductCard {
  @apply relative h-full w-full;
  container: productCard / inline-size;

  &:hover {
    .LiteProductCard__image {
      @apply scale-[1.03];
    }

    @screen md {
      .LiteProductCard__custom-btn-text {
        @apply max-w-28 opacity-100 pl-2;
      }
    }
  }

  &.LiteProductCard--with-swatches {
    &:hover {
      .LiteProductCard__title-wrapper {
        @apply md:opacity-0;
      }

      .LiteProductCard__variants-wrapper {
        @screen md {
          @apply opacity-100 pointer-events-auto;
        };
      }
    }
  }
}

.LiteProductCard--fade-contents {
  .LiteProductCard__media,
  .LiteProductCard__title,
  .LiteProductCard__review {
    @apply opacity-40;
  }
}

.LiteProductCard--with-discount {
  .LiteProductCard__base-price {
    @apply text-[9px] sm:text-[11px] opacity-60;
  }

  .LiteProductCard__discounted-price {
    @apply mr-[3px];
  }
}

.LiteProductCard--compact-view {
  .LiteProductCard__price {
    @apply mt-0;
  }

  .LiteProductCard__title-suffix,
  .LiteProductCard__subtitle {
    @apply hidden;
  }

  .LiteProductCard__variants, .LiteProductCard__variants-wrapper {
    @apply gap-x-1;
  }

  .LiteProductCard__lens-tech-badge {
    @apply hidden;
  }

  &:hover {
    .LiteProductCard__body {
      @apply md:pt-2;
    }
  }

  @media (max-width: 499px) {
    .LiteProductCard__price,
    .LiteProductCard__title {
      @apply text-[12px];
    }

    .LiteProductCard__variants {
      @apply gap-x-1;
    }
  }
}

.LiteProductCard__media {
  @apply relative aspect-square shrink-0 rounded-lg overflow-hidden;

  background: linear-gradient(180deg, #f9f9f9 55%, #f2f2f5 80%);
}

.LiteProductCard__custom-btn {
  @apply flex items-center justify-end absolute bg-white rounded-full overflow-hidden top-1 right-1 h-6;

  &::before {
    @apply hidden content-[''] absolute inset-0 rounded-full border border-grey-400 z-10;
  }

  &:hover {
    &::before {
      @apply block;
    }
  }

  @screen md {
    @apply h-8 top-2 right-3;
  }
}

.LiteProductCard__custom-btn-text {
  @apply uppercase text-[10px] font-bold max-w-0 opacity-0 transition-all duration-300 py-2 pl-0 pr-0;
}

.LiteProductCard__custom-btn-icon-wrapper {
  @apply relative flex items-center justify-center bg-white rounded-full flex-shrink-0 w-6 h-6;

  @screen md {
    @apply w-8 h-8;
  }
}

.LiteProductCard__custom-btn-icon {
  @apply w-3 h-3;

  @screen md {
    @apply w-4 h-4;
  }
}

.LiteProductCard__image {
  @apply w-full transition-all duration-200 rounded-lg overflow-hidden absolute;
}

.LiteProductCard__image--front-angle {
  @apply opacity-0;
}

.LiteProductCard__front-angle-animation-wrapper {
  @apply opacity-0;
}

.LiteProductCard__media--front-angle-loaded {
  .LiteProductCard__front-angle-animation-wrapper {
    /* 200ms matches the fade out duration on the side image fading out. */
    animation: fade-in 500ms 200ms forwards;
  }

  .LiteProductCard__image--front-angle {
    @apply opacity-100;
  }

  .LiteProductCard__image--previous-image,
  .LiteProductCard__image--side-angle {
    @apply opacity-0;
  }
}

.LiteProductCard__media--loading {
  &.LiteProductCard__media--front-angle {
    @apply animate-pulse;

    .LiteProductCard__image--front-angle {
      @apply opacity-50;
    }
  }

  &:not(.LiteProductCard__media--front-angle-loaded) {
    .LiteProductCard__image--side-angle {
      @apply opacity-50;
    }
  }

  &.LiteProductCard__media--side-angle {
    .LiteProductCard__image--side-angle {
      @apply opacity-50;
    }
  }
}

.LiteProductCard__media-overlay-item {
  @apply absolute;

  width: 40%;
  bottom: 5%;
  right: 5%;
}

.LiteProductCard__lens-tech-badge {
  @apply absolute bottom-2 md:bottom-3 left-2 md:left-4;

  --vision-tag-text-color: theme(colors.black);

  @screen smMax {
    --vision-tag-font-size: 7px;
  }
}

.LiteProductCard__lens-tech-badge-content {
  @apply flex items-center smMax:gap-0.5 gap-1 text-grey;
}

.LiteProductCard__lens-tech-badge-content--no-icon {
  @apply t-gradient;
}

.LiteProductCard__lens-tech-badge-icon {
  @apply smMax:w-3.5 w-5 h-2;
}

.LiteProductCard__lens-tech-badge--snow {
  @apply smMax:w-10 w-12;
}

.LiteProductCard__body {
  --title-size: theme('fontSize.xs');
  --price-size: theme('fontSize.xxs');
  --padding-width: 4px;
  --swatch-width: 32px;
  --swatch-border-radius: 4px;
  @apply pt-2 flex flex-col basis-full;
}

@container productCard (min-width: 220px) {
  .LiteProductCard__body {
    --title-size: theme('fontSize.base');
    --price-size: theme('fontSize.xs');
    --swatch-width: 40px;
    --padding-width: 8px;
  }
}

@container productCard (min-width: 360px) {
  .LiteProductCard__body {
    --title-size: theme('fontSize.lg');
    --swatch-width: 56px;
    --swatch-border-radius: 6px;
  }
}


.LiteProductCard__title-wrapper {
  @apply flex justify-between items-end px-[var(--padding-width)];
}

.LiteProductCard__title {
  @apply font-bold text-off-black w-full;
  font-size: var(--title-size);
}

.LiteProductCard__title-content {
  @apply flex gap-2 items-center justify-between w-full;
}

.LiteProductCard__title-suffix {
  @apply font-thin;
}

.LiteProductCard__subtitle {
  @apply text-grey-500 sm:mt-1;
}

.LiteProductCard__title-suffix,
.LiteProductCard__subtitle,
.LiteProductCard__review-count {
  @apply font-fragment-mono text-tiny sm:text-xxs;
}

.LiteProductCard__price {
  @apply text-off-black font-thin mb-0;
  font-size: var(--price-size);
}

.LiteProductCard__tag {
  @apply absolute top-2 left-2;

  @screen smMax {
    --vision-tag-font-size: 7px;
  }
}

.LiteProductCard__variants-wrapper {
  @apply flex absolute opacity-0 pointer-events-none gap-1.5;
}

.LiteProductCard__variants {
  @apply flex gap-1.5;
}

.LiteProductCard__variant {
  @apply flex flex-grow-0 items-center justify-center
    border border-transparent bg-grey-200 hover:border-grey-600
    rounded-[var(--swatch-border-radius)]
    overflow-hidden
    w-[var(--swatch-width)]
    aspect-square
    text-center text-xxs text-grey-500;
}

.LiteProductCard__variant--active {
  @apply border-grey-800 bg-white hover:border-grey-800;
}

.LiteProductCard__variant--custom {
  &:hover {
    @apply border-grey-800 bg-white text-black;
  }
}

.LiteProductCard:has(.LiteProductCard__variant--custom:hover) {
  .LiteProductCard__variant--active {
    @apply border-transparent bg-grey-200;
  }
}

@keyframes fade-in {
  100% {
    @apply opacity-100;
  }
}
</style>
